import { Component, inject } from '@angular/core';
import { PermissionService } from 'src/app/core/services/auth/permission.service';
import { SsoService } from 'src/app/core/services/sso.service';
import { SessionStoreService } from 'src/app/core/services/stores/session-store.service';
import { FeatureFlagsNames } from 'src/app/shared/constants/feature-flags.constants';
import { BusinessPermissions, ContractingPermissions, CoreCommissionsPermissions, CpcReportingPermissions, DolViewPermissions, EventsPermissions, LumaPermissions, MarketingPermissions, MarketingTrackerPermissions, MarketplaceOnePermissions, StructuredMarketplacePermissions } from 'src/app/shared/constants/permissions-shorthand.constants';
import { ResourcePageRouteId } from 'src/app/shared/enums/route-name.enum';
import { AmplitudeDirectiveObject } from 'src/app/shared/models/amplitude.models';
import { RouteTypeEnum } from 'src/app/shared/models/custom-routes.models';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-main-nav-v2',
  templateUrl: './main-nav-v2.component.html',
  styles: [':host {@apply block h-full;}'],
  standalone: false
})
export class MainNavV2Component {
  private readonly ssoService = inject(SsoService);
  private readonly permissionService = inject(PermissionService);
  private readonly sessionStore = inject(SessionStoreService);

  routeType = RouteTypeEnum;
  amplitudeEventOptions: AmplitudeDirectiveObject = {
    EventType: 'Main Nav Click',
  };
  environment = environment;
  nav: MainNavV2[] = [
    {
      name: 'Dashboard',
      path: 'Dashboard',
      type: RouteTypeEnum.internal,
      visible: (): boolean => true,
    },
    {
      name: 'Marketing',
      type: RouteTypeEnum.none,
      visible: (): boolean => this.permissionService.hasOnePermission(MarketingPermissions),
      children: [
        {
          name: 'Marketplace One',
          action: (): void => this.ssoService.marketPlaceOneSsoV2(),
          type: RouteTypeEnum.action,
          visible: (): boolean => this.permissionService.hasOnePermission(MarketplaceOnePermissions),
        },
        {
          name: 'Marketing Tracker',
          path: window.location.origin + '/v1/#/Tools/MarketingTrackerNew',
          type: RouteTypeEnum.external,
          visible: (): boolean => this.permissionService.hasOnePermission(MarketingTrackerPermissions),
        },
        {
          name: 'new Portal',
          path: window.location.origin + '/v2',
          type: RouteTypeEnum.external,
          visible: (): boolean => !!this.sessionStore.UserFeatureFlag?.find(x => x.FeatureFlag === FeatureFlagsNames.showTrackerPilot)?.FlagEnabled,
        },
      ]
    },
    {
      name: 'Contracting',
      path: 'Contracting',
      type: RouteTypeEnum.internal,
      visible: (): boolean => true,
      children: [
        {
          name: 'Status & Writing Number',
          path: 'Status',
          type: RouteTypeEnum.internal,
          visible: (): boolean => this.permissionService.hasOnePermission(ContractingPermissions),
        },
        {
          name: 'Training',
          path: 'https://www.training.figmarketing.com/',
          type: RouteTypeEnum.external,
          visible: (): boolean => true,
        },
      ]
    },
    {
      name: 'Business',
      path: 'Business',
      type: RouteTypeEnum.internal,
      visible: (): boolean => this.permissionService.hasOnePermission(BusinessPermissions.newBusiness),
      children: [
        {
          name: 'E-Applications',
          path: 'EApplications',
          type: RouteTypeEnum.internal,
          visible: (): boolean => this.permissionService.hasOnePermission(BusinessPermissions.eApps),
        },
        {
          name: 'Pending',
          path: 'Fixed/Pending',
          type: RouteTypeEnum.internal,
          visible: (): boolean => true,
        },
        {
          name: 'Inforce',
          path: 'Fixed/Inforce',
          type: RouteTypeEnum.internal,
          visible: (): boolean => true,
        },
        {
          name: 'Lifecycle',
          path: 'Fixed/Lifecycle',
          type: RouteTypeEnum.internal,
          visible: (): boolean => this.permissionService.hasAllPermissions(BusinessPermissions.lifecycleV2),
        },
        {
          name: 'CPC Reporting',
          path: 'Fixed/CPCReporting',
          type: RouteTypeEnum.internal,
          visible: (): boolean => this.permissionService.hasOnePermission(CpcReportingPermissions),
        },
        {
          name: 'Informal',
          path: 'Fixed/Informal',
          type: RouteTypeEnum.internal,
          visible: (): boolean => true,
        },
        {
          name: 'Wealth',
          path: 'Wealth',
          type: RouteTypeEnum.internal,
          visible: (): boolean => !!this.sessionStore.UserAcmAgent,
        },
      ]
    },
    {
      name: 'Resources',
      path: 'Resources',
      type: RouteTypeEnum.internal,
      visible: (): boolean => true,
      children: [
        {
          name: 'Tools & Calculators',
          path: `${ResourcePageRouteId.Tools}`,
          type: RouteTypeEnum.internal,
          visible: (): boolean => true,
        },
        {
          name: 'Forms & Guides',
          path: `${ResourcePageRouteId.Forms}`,
          type: RouteTypeEnum.internal,
          visible: (): boolean => true,
        },
        {
          name: 'Structured Notes',
          path: '/api/LumaSso?destination=Lifecyclemanager',
          type: RouteTypeEnum.external,
          visible: (): boolean => this.permissionService.hasAllPermissions([...StructuredMarketplacePermissions, ...LumaPermissions]),
        },
        {
          name: 'Events',
          path: 'https://www.meetwithfig.com/',
          type: RouteTypeEnum.external,
          visible: (): boolean => this.permissionService.hasAllPermissions([...EventsPermissions]),
        },
        {
          name: 'Gov & Regulatory Updates',
          path: 'Details/RegulatoryUpdates',
          type: RouteTypeEnum.internal,
          visible: (): boolean => this.permissionService.hasOnePermission(DolViewPermissions),
        },
        {
          name: 'FIGures - Commissions',
          path: '/api/CoreCommissionsSso',
          type: RouteTypeEnum.external,
          visible: (): boolean => this.permissionService.hasOnePermission(CoreCommissionsPermissions),
        },
      ]
    },
  ];
}

export interface MainNavV2 {
  name: string;
  visible: () => boolean;
  type: RouteTypeEnum,
  children?: MainNavV2[];
  path?: string;
  routeParams?: string;
  action?: () => void;
}